import { CircularProgress } from "@mui/material";
import { useTranslations } from "use-intl";
import type {
  GetFlightAvailabilityTokenBaggageDetailsV2,
  GetFlightAvailabilityTokenDirectionalBaggageV2,
} from "../lib/types/codegen";
import {
  IconCarryOnBaggage,
  IconCarryOnBaggageNotIncluded,
  IconCheckedBaggage,
  IconCheckedBaggageNotIncluded,
  IconHandBag,
  IconHandBagNotIncluded,
} from "../icons";

type BaggageIconProps = {
  baggageType: "checked" | "carryOn" | "handBag";
  disabled?: boolean;
};

const BaggageIcon = ({ baggageType, disabled }: BaggageIconProps) => {
  const IconComponent =
    baggageType === "carryOn"
      ? disabled
        ? IconCarryOnBaggageNotIncluded
        : IconCarryOnBaggage
      : baggageType === "handBag"
        ? disabled
          ? IconHandBagNotIncluded
          : IconHandBag
        : disabled
          ? IconCheckedBaggageNotIncluded
          : IconCheckedBaggage;

  return <IconComponent className="size-8 shrink-0" aria-hidden />;
};

type BaggageDetailsProps = {
  baggageType: "checked" | "carryOn" | "handBag";
  data: GetFlightAvailabilityTokenBaggageDetailsV2;
};

const BaggageDetails = ({ baggageType, data }: BaggageDetailsProps) => {
  const t = useTranslations();

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-1">
        <BaggageIcon baggageType={baggageType} />

        <div>
          <div className="font-medium">
            {baggageType === "carryOn"
              ? t("flights.baggage.carryOnBaggage")
              : baggageType === "handBag"
                ? t("flights.baggage.handBag")
                : t("flights.baggage.checkedBaggage")}
            {data.weightKg
              ? `: ${t("flights.baggage.upToXKg", { kg: data.weightKg })}`
              : `: ${t("flights.baggage.included")}`}
          </div>

          {data.dimensionsCm ? (
            <div className="mt-0.5 text-text-disabled-secondary">
              {t("flights.baggage.upToXCmInTotal", {
                cm: data.dimensionsCm,
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="text-text-disabled-secondary">
        {data.numberOfPieces
          ? t("flights.baggage.xPieces", {
              count: data.numberOfPieces,
            })
          : null}
      </div>
    </div>
  );
};

type NoBaggageDetailsProps = {
  baggageType: "checked" | "carryOn";
};

const NoBaggageDetails = ({ baggageType }: NoBaggageDetailsProps) => {
  const t = useTranslations();

  return (
    <div className="flex items-center gap-1 text-sm font-medium">
      <BaggageIcon baggageType={baggageType} disabled />
      <div>
        {baggageType === "carryOn"
          ? t("flights.baggage.noFreeCarryOnBaggage")
          : t("flights.baggage.noFreeCheckedBaggage")}
      </div>
    </div>
  );
};

type Props = {
  baggageData?: GetFlightAvailabilityTokenDirectionalBaggageV2;
  isLoading?: boolean;
};

export const BaggageAllowance = ({ baggageData, isLoading }: Props) => {
  const t = useTranslations();

  return (
    <div className="section-card text-text-primary">
      {isLoading ? (
        <div className="flex w-full items-center gap-2 text-sm">
          <CircularProgress size={20} />
          {t("flights.baggage.retrievingBaggageDetails")}
        </div>
      ) : (
        <>
          <h3 className="mb-4 text-base font-medium">
            {t("flights.baggage.baggageAllowance")}
          </h3>

          <ul className="space-y-2 *:not-last:border-b *:not-last:border-b-stroke-neutral-soft *:not-last:pb-2">
            {baggageData?.carryOn && baggageData.carryOn.length > 0 ? (
              baggageData.carryOn.map((carryOnBaggage, index) => (
                <li key={index}>
                  <BaggageDetails
                    baggageType={
                      carryOnBaggage.isSmallBag ? "handBag" : "carryOn"
                    }
                    data={carryOnBaggage}
                  />
                </li>
              ))
            ) : (
              <li>
                <NoBaggageDetails baggageType="carryOn" />
              </li>
            )}

            {baggageData?.baggage && baggageData.baggage.length > 0 ? (
              baggageData.baggage.map((checkedBaggage, index) => (
                <li key={index}>
                  <BaggageDetails baggageType="checked" data={checkedBaggage} />
                </li>
              ))
            ) : (
              <li>
                <NoBaggageDetails baggageType="checked" />
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};
